import React, {
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  import {
    collection,
    query,
    where,
    getDocs,
    updateDoc,
    addDoc,
    serverTimestamp,
  } from "firebase/firestore";
  
  import { db } from "../firebase";
  
  import {
    useLocation,
    useNavigate,
  } from "react-router-dom";
  
  import "./CryptoWallet.css";
  
  /*
  |--------------------------------------------------------------------------
  | SERVER URL
  |--------------------------------------------------------------------------
  */
  
  const SERVER_URL =
    process.env.REACT_APP_SERVER_URL ||
    `http://${window.location.hostname}:5000`;
  
  /*
  |--------------------------------------------------------------------------
  | AMOUNT SHORTCUTS
  |--------------------------------------------------------------------------
  */
  
  const AMOUNT_SHORTCUTS = [
    10,
    30,
    50,
    75,
    100,
    150,
    200,
    250,
    300,
    500,
    750,
    1000,
    1500,
    2000,
    5000,
  ];
  
  /*
  |--------------------------------------------------------------------------
  | DEPOSIT METHODS
  |--------------------------------------------------------------------------
  */
  
  const DEPOSIT_METHODS = [
    {
      id: "TRC20",
      name: "Tether (USDT)",
      network: "TRC-20",
      coin: "USDT",
      field: "trc20Address",
      walletKey: "TRC20",
      min: 10,
    },
  
    {
      id: "TRON_TRX",
      name: "Tron",
      network: "TRON",
      coin: "TRX",
      field: "trxAddress",
      walletKey: "TRON_TRX",
      min: 10,
    },
  
    {
      id: "ERC20",
      name: "Ethereum",
      network: "ERC-20",
      coin: "ETH",
      field: "erc20Address",
      walletKey: "ERC20",
      min: 10,
    },
  
    {
      id: "BNB_BEP20",
      name: "BNB Smart Chain",
      network: "BEP20 BSC",
      coin: "BNB",
      field: "bep20Address",
      walletKey: "BNB_BEP20",
      min: 10,
    },
  
    {
      id: "BITCOIN",
      name: "Bitcoin",
      network: "BTC",
      coin: "BTC",
      field: "bitcoinAddress",
      walletKey: "BITCOIN",
      min: 10,
    },
  
    {
      id: "SOLANA",
      name: "Solana",
      network: "SOLANA",
      coin: "SOL",
      field: "solanaAddress",
      walletKey: "SOLANA",
      min: 10,
    },
  ];
  
  /*
  |--------------------------------------------------------------------------
  | TRANSACTION ID
  |--------------------------------------------------------------------------
  */
  
  const createTransactionId = () => {
    const timestamp = Date.now();
  
    const random =
      Math.random()
        .toString(36)
        .substring(2, 10)
        .toUpperCase();
  
    return `VF-${timestamp}-${random}`;
  };
  
  /*
  |--------------------------------------------------------------------------
  | COIN LOGO
  |--------------------------------------------------------------------------
  */
  
  const CoinLogo = ({ type }) => {
    const common = {
      width: 42,
      height: 42,
      viewBox: "0 0 40 40",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
    };
  
    /*
    |--------------------------------------------------------------------------
    | USDT
    |--------------------------------------------------------------------------
    */
  
    if (type === "USDT") {
      return (
        <svg {...common}>
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#26A17B"
          />
  
          <path
            d="M10 10.5H30V15H23V27.5H17V15H10V10.5Z"
            fill="white"
          />
  
          <path
            d="M20 15C25 15 29 16 29 17.5C29 19 25 20 20 20C15 20 11 19 11 17.5C11 16 15 15 20 15Z"
            fill="#26A17B"
          />
  
          <path
            d="M20 20V29"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | TRX
    |--------------------------------------------------------------------------
    */
  
    if (type === "TRX") {
      return (
        <svg {...common}>
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#EC1C24"
          />
  
          <path
            d="M10 10L30 13L22 30L10 10Z"
            fill="white"
          />
  
          <path
            d="M10 10L22 18L30 13"
            stroke="#EC1C24"
            strokeWidth="1.5"
          />
  
          <path
            d="M22 18L22 30"
            stroke="#EC1C24"
            strokeWidth="1.5"
          />
        </svg>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | ETH
    |--------------------------------------------------------------------------
    */
  
    if (type === "ETH") {
      return (
        <svg {...common}>
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#627EEA"
          />
  
          <path
            d="M20 6L12.5 20L20 24.5L27.5 20L20 6Z"
            fill="white"
          />
  
          <path
            d="M20 25.8L12.5 21.5L20 34L27.5 21.5L20 25.8Z"
            fill="white"
            opacity="0.7"
          />
        </svg>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | BNB
    |--------------------------------------------------------------------------
    */
  
    if (type === "BNB") {
      return (
        <svg {...common}>
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#F3BA2F"
          />
  
          <path
            d="M20 8L24 12L20 16L16 12L20 8Z"
            fill="#111827"
          />
  
          <path
            d="M13 15L16 18L13 21L10 18L13 15Z"
            fill="#111827"
          />
  
          <path
            d="M27 15L30 18L27 21L24 18L27 15Z"
            fill="#111827"
          />
  
          <path
            d="M20 15L25 20L20 25L15 20L20 15Z"
            fill="#111827"
          />
  
          <path
            d="M20 24L24 28L20 32L16 28L20 24Z"
            fill="#111827"
          />
        </svg>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | BTC
    |--------------------------------------------------------------------------
    */
  
    if (type === "BTC") {
      return (
        <svg {...common}>
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#F7931A"
          />
  
          <text
            x="20"
            y="27"
            textAnchor="middle"
            fontSize="22"
            fontWeight="900"
            fill="white"
            fontFamily="Arial"
          >
            ₿
          </text>
        </svg>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | SOL
    |--------------------------------------------------------------------------
    */
  
    if (type === "SOL") {
      return (
        <svg {...common}>
          <defs>
            <linearGradient
              id="solGradientWallet"
              x1="5"
              y1="5"
              x2="35"
              y2="35"
            >
              <stop
                offset="0%"
                stopColor="#00FFA3"
              />
  
              <stop
                offset="100%"
                stopColor="#9945FF"
              />
            </linearGradient>
          </defs>
  
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="#111827"
          />
  
          <path
            d="M10 13H29L25.5 16.5H10L13.5 13Z"
            fill="url(#solGradientWallet)"
          />
  
          <path
            d="M11 19H30L26.5 22.5H11L14.5 19Z"
            fill="url(#solGradientWallet)"
          />
  
          <path
            d="M10 25H29L25.5 28.5H10L13.5 25Z"
            fill="url(#solGradientWallet)"
          />
        </svg>
      );
    }
  
    return null;
  };
  
  /*
  |--------------------------------------------------------------------------
  | PAGE
  |--------------------------------------------------------------------------
  */
  
  export default function CryptoWallet() {
    const navigate = useNavigate();
    const location = useLocation();
  
    /*
    |--------------------------------------------------------------------------
    | METHOD FROM STATE
    |--------------------------------------------------------------------------
    */
  
    const methodFromState =
      location.state?.method;
  
    const existingFromState =
      location.state?.existingAddress || "";
  
    /*
    |--------------------------------------------------------------------------
    | SELECTED METHOD
    |--------------------------------------------------------------------------
    */
  
    const selected = useMemo(() => {
      if (!methodFromState) {
        return null;
      }
  
      return (
        DEPOSIT_METHODS.find(
          (item) =>
            item.id ===
            methodFromState.id
        ) || methodFromState
      );
    }, [methodFromState]);
  
    /*
    |--------------------------------------------------------------------------
    | STATES
    |--------------------------------------------------------------------------
    */
  
    const [username, setUsername] =
      useState("");
  
    const [userDocId, setUserDocId] =
      useState("");
  
    const [address, setAddress] =
      useState(existingFromState);
  
    const [amount, setAmount] =
      useState("");
  
    const [loading, setLoading] =
      useState(true);
  
    const [
      generatingWallet,
      setGeneratingWallet,
    ] = useState(false);
  
    const [
      depositLoading,
      setDepositLoading,
    ] = useState(false);
  
    const [
      showDepositDetails,
      setShowDepositDetails,
    ] = useState(false);
  
    const [
      showAllAmountShortcuts,
      setShowAllAmountShortcuts,
    ] = useState(false);
  
    const [copied, setCopied] =
      useState(false);
  
    const [error, setError] =
      useState("");
  
    const [message, setMessage] =
      useState("");
  
    const [
      transactionId,
      setTransactionId,
    ] = useState("");
  
    const [
      depositSubmitted,
      setDepositSubmitted,
    ] = useState(false);
  
    /*
    |--------------------------------------------------------------------------
    | LOAD USER
    |--------------------------------------------------------------------------
    */
  
    useEffect(() => {
      if (!selected) {
        navigate(
          "/deposit-crypto",
          {
            replace: true,
          }
        );
  
        return;
      }
  
      loadUser();
  
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);
  
    /*
    |--------------------------------------------------------------------------
    | LOAD USER
    |--------------------------------------------------------------------------
    */
  
    const loadUser = async () => {
      try {
        setLoading(true);
        setError("");
  
        const savedUsername =
          localStorage.getItem(
            "username"
          );
  
        if (!savedUsername) {
          setError(
            "You are not logged in."
          );
  
          setLoading(false);
          return;
        }
  
        setUsername(savedUsername);
  
        const q = query(
          collection(
            db,
            "users"
          ),
          where(
            "username",
            "==",
            savedUsername
          )
        );
  
        const snapshot =
          await getDocs(q);
  
        if (snapshot.empty) {
          setError(
            "User account was not found."
          );
  
          setLoading(false);
          return;
        }
  
        const userDoc =
          snapshot.docs[0];
  
        const data =
          userDoc.data();
  
        setUserDocId(
          userDoc.id
        );
  
        /*
        |--------------------------------------------------------------------------
        | EXISTING WALLET
        |--------------------------------------------------------------------------
        */
  
        if (selected) {
          const oldAddress =
            data?.[
              selected.field
            ];
  
          const walletAddress =
            data?.cryptoWallets?.[
              selected.walletKey
            ]?.address;
  
          const finalAddress =
            oldAddress ||
            walletAddress ||
            "";
  
          if (
            finalAddress &&
            String(
              finalAddress
            ).trim()
          ) {
            setAddress(
              String(
                finalAddress
              ).trim()
            );
          }
        }
  
        setLoading(false);
      } catch (err) {
        console.error(
          "Load wallet user error:",
          err
        );
  
        setError(
          "Failed to load your account."
        );
  
        setLoading(false);
      }
    };
  
    /*
    |--------------------------------------------------------------------------
    | GENERATE WALLET
    |--------------------------------------------------------------------------
    */
  
    const generateWallet =
      async () => {
        if (!selected) {
          return;
        }
  
        if (generatingWallet) {
          return;
        }
  
        try {
          setGeneratingWallet(
            true
          );
  
          setError("");
          setMessage("");
  
          /*
          |--------------------------------------------------------------------------
          | GET LATEST USER
          |--------------------------------------------------------------------------
          */
  
          const q = query(
            collection(
              db,
              "users"
            ),
            where(
              "username",
              "==",
              username
            )
          );
  
          const snapshot =
            await getDocs(q);
  
          if (snapshot.empty) {
            throw new Error(
              "User document could not be found."
            );
          }
  
          const userDoc =
            snapshot.docs[0];
  
          const latestUserData =
            userDoc.data();
  
          /*
          |--------------------------------------------------------------------------
          | EXISTING WALLET
          |--------------------------------------------------------------------------
          */
  
          const existingAddress =
            latestUserData?.[
              selected.field
            ] ||
            latestUserData?.cryptoWallets?.[
              selected.walletKey
            ]?.address ||
            "";
  
          if (
            existingAddress &&
            String(
              existingAddress
            ).trim()
          ) {
            setAddress(
              String(
                existingAddress
              ).trim()
            );
  
            setUserDocId(
              userDoc.id
            );
  
            return;
          }
  
          /*
          |--------------------------------------------------------------------------
          | WALLET INDEX
          |--------------------------------------------------------------------------
          */
  
          let walletIndex =
            latestUserData?.walletIndex;
  
          if (
            walletIndex ===
              undefined ||
            walletIndex === null ||
            walletIndex === ""
          ) {
            walletIndex =
              Math.floor(
                Date.now() %
                  1000000
              );
          }
  
          /*
          |--------------------------------------------------------------------------
          | WALLET SERVER
          |--------------------------------------------------------------------------
          */
  
          const response =
            await fetch(
              `${SERVER_URL}/wallet`,
              {
                method: "POST",
  
                headers: {
                  "Content-Type":
                    "application/json",
                },
  
                body: JSON.stringify({
                  index:
                    walletIndex,
                }),
              }
            );
  
          if (!response.ok) {
            throw new Error(
              `Wallet server returned ${response.status}`
            );
          }
  
          const data =
            await response.json();
  
          if (
            !data?.success ||
            !data?.wallets
          ) {
            throw new Error(
              "Wallet generation failed."
            );
          }
  
          const wallets =
            data.wallets;
  
          /*
          |--------------------------------------------------------------------------
          | SELECT ADDRESS
          |--------------------------------------------------------------------------
          */
  
          const generatedAddress =
            wallets?.[
              selected.walletKey
            ]?.address || "";
  
          if (!generatedAddress) {
            throw new Error(
              `No ${selected.coin} address was returned by wallet server.`
            );
          }
  
          /*
          |--------------------------------------------------------------------------
          | PRESERVE WALLETS
          |--------------------------------------------------------------------------
          */
  
          const cryptoWallets = {
            ...(
              latestUserData?.cryptoWallets ||
              {}
            ),
            ...wallets,
          };
  
          /*
          |--------------------------------------------------------------------------
          | FIRESTORE USER DATA
          |--------------------------------------------------------------------------
          */
  
          const updateData = {
            walletIndex,
  
            cryptoWallets,
  
            trc20Address:
              wallets?.TRC20
                ?.address ||
              latestUserData?.trc20Address ||
              "",
  
            trxAddress:
              wallets?.TRON_TRX
                ?.address ||
              latestUserData?.trxAddress ||
              "",
  
            erc20Address:
              wallets?.ERC20
                ?.address ||
              latestUserData?.erc20Address ||
              "",
  
            bep20Address:
              wallets?.BNB_BEP20
                ?.address ||
              latestUserData?.bep20Address ||
              "",
  
            bitcoinAddress:
              wallets?.BITCOIN
                ?.address ||
              latestUserData?.bitcoinAddress ||
              "",
  
            solanaAddress:
              wallets?.SOLANA
                ?.address ||
              latestUserData?.solanaAddress ||
              "",
          };
  
          /*
          |--------------------------------------------------------------------------
          | SAVE USER WALLET
          |--------------------------------------------------------------------------
          */
  
          await updateDoc(
            userDoc.ref,
            updateData
          );
  
          /*
          |--------------------------------------------------------------------------
          | UPDATE STATE
          |--------------------------------------------------------------------------
          */
  
          setUserDocId(
            userDoc.id
          );
  
          setAddress(
            generatedAddress
          );
  
          setMessage(
            `${selected.name} wallet created successfully.`
          );
        } catch (err) {
          console.error(
            "Generate wallet error:",
            err
          );
  
          if (
            err?.message?.includes(
              "Failed to fetch"
            )
          ) {
            setError(
              `Cannot connect to wallet server. Make sure backend is running on ${SERVER_URL}`
            );
          } else {
            setError(
              err?.message ||
                "Failed to create wallet."
            );
          }
        } finally {
          setGeneratingWallet(
            false
          );
        }
      };
  
    /*
    |--------------------------------------------------------------------------
    | CONTINUE
    |--------------------------------------------------------------------------
    */
  
    const handleContinue = (
      e
    ) => {
      e.preventDefault();
  
      setError("");
      setMessage("");
  
      const numericAmount =
        Number(amount);
  
      /*
      |--------------------------------------------------------------------------
      | VALIDATE AMOUNT
      |--------------------------------------------------------------------------
      */
  
      if (
        !numericAmount ||
        numericAmount <
          selected.min
      ) {
        setError(
          `Minimum deposit is $${selected.min}.`
        );
  
        return;
      }
  
      /*
      |--------------------------------------------------------------------------
      | WALLET MUST EXIST
      |--------------------------------------------------------------------------
      */
  
      if (!address) {
        setError(
          "Wallet address is not ready."
        );
  
        return;
      }
  
      /*
      |--------------------------------------------------------------------------
      | SHOW DEPOSIT DETAILS
      |--------------------------------------------------------------------------
      */
  
      setShowDepositDetails(
        true
      );
    };
  
    /*
    |--------------------------------------------------------------------------
    | COPY ADDRESS
    |--------------------------------------------------------------------------
    */
  
    const copyAddress =
      async () => {
        if (!address) {
          return;
        }
  
        try {
          if (
            navigator.clipboard &&
            window.isSecureContext
          ) {
            await navigator.clipboard.writeText(
              address
            );
          } else {
            const textArea =
              document.createElement(
                "textarea"
              );
  
            textArea.value =
              address;
  
            textArea.style.position =
              "fixed";
  
            textArea.style.left =
              "-9999px";
  
            textArea.style.top =
              "0";
  
            textArea.style.opacity =
              "0";
  
            document.body.appendChild(
              textArea
            );
  
            textArea.focus();
            textArea.select();
  
            document.execCommand(
              "copy"
            );
  
            document.body.removeChild(
              textArea
            );
          }
  
          setCopied(true);
  
          setTimeout(() => {
            setCopied(false);
          }, 2200);
        } catch (err) {
          console.error(
            "Copy failed:",
            err
          );
  
          setError(
            "Failed to copy wallet address. Please try again."
          );
        }
      };
  
    /*
    |--------------------------------------------------------------------------
    | QR CODE
    |--------------------------------------------------------------------------
    */
  
    const qrUrl = address
      ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
          address
        )}`
      : "";
  
    /*
    |--------------------------------------------------------------------------
    | DEPOSIT REQUEST
    |--------------------------------------------------------------------------
    */
  
    const handleDepositRequest =
      async (e) => {
        e.preventDefault();
  
        if (!selected) {
          setError(
            "Deposit method is missing."
          );
  
          return;
        }
  
        if (!address) {
          setError(
            "Wallet address is not ready."
          );
  
          return;
        }
  
        const numericAmount =
          Number(amount);
  
        if (
          !numericAmount ||
          numericAmount <
            selected.min
        ) {
          setError(
            `Minimum deposit is $${selected.min}.`
          );
  
          return;
        }
  
        /*
        |--------------------------------------------------------------------------
        | PREVENT DUPLICATE SUBMISSION
        |--------------------------------------------------------------------------
        */
  
        if (depositSubmitted) {
          return;
        }
  
        try {
          setDepositLoading(
            true
          );
  
          setError("");
          setMessage("");
  
          /*
          |--------------------------------------------------------------------------
          | CREATE TRANSACTION ID
          |--------------------------------------------------------------------------
          */
  
          const newTransactionId =
            createTransactionId();
  
          /*
          |--------------------------------------------------------------------------
          | SAVE TO VOLATILITYFOREX
          |--------------------------------------------------------------------------
          */
  
          await addDoc(
            collection(
              db,
              "volatilityforex"
            ),
            {
              transactionId:
                newTransactionId,
  
              username,
  
              userId:
                userDocId,
  
              type:
                "deposit",
  
              status:
                "processing",
  
              method:
                selected.id,
  
              coin:
                selected.coin,
  
              crypto:
                selected.coin,
  
              network:
                selected.network,
  
              amount:
                numericAmount,
  
              currency:
                "USD",
  
              walletAddress:
                address,
  
              depositAddress:
                address,
  
              walletType:
                "crypto",
  
              createdAt:
                serverTimestamp(),
  
              updatedAt:
                serverTimestamp(),
            }
          );
  
          /*
          |--------------------------------------------------------------------------
          | UPDATE STATE
          |--------------------------------------------------------------------------
          */
  
          setTransactionId(
            newTransactionId
          );
  
          setDepositSubmitted(
            true
          );
  
          setMessage(
            "Deposit request submitted successfully. Waiting for confirmation."
          );
        } catch (err) {
          console.error(
            "Deposit request error:",
            err
          );
  
          setError(
            "Failed to submit deposit request."
          );
        } finally {
          setDepositLoading(
            false
          );
        }
      };
  
    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */
  
    if (loading) {
      return (
        <div className="crypto-wallet-page">
  
          <div className="crypto-wallet-loading">
  
            <div className="deposit-spinner" />
  
            <p>
              Loading wallet...
            </p>
  
          </div>
  
        </div>
      );
    }
  
    /*
    |--------------------------------------------------------------------------
    | NO METHOD
    |--------------------------------------------------------------------------
    */
  
    if (!selected) {
      return null;
    }
  
    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */
  
    return (
      <div className="crypto-wallet-page">
  
        <div className="crypto-wallet-container">
  
          {/* =====================================================
              HEADER
          ===================================================== */}
  
          <div className="crypto-wallet-header">
  
            <button
              type="button"
              className="crypto-wallet-back"
              onClick={() =>
                navigate(-1)
              }
            >
              ←
            </button>
  
            <div>
  
              <span>
                CRYPTO DEPOSIT
              </span>
  
              <h1>
                {selected.name}
              </h1>
  
              <p>
                {selected.network}
              </p>
  
            </div>
  
            <div className="crypto-wallet-icon">
  
              <CoinLogo
                type={
                  selected.coin
                }
              />
  
            </div>
  
          </div>
  
          {/* =====================================================
              ERROR
          ===================================================== */}
  
          {error && (
            <div className="crypto-wallet-alert error">
  
              <span>
                !
              </span>
  
              <p>
                {error}
              </p>
  
              <button
                type="button"
                onClick={() =>
                  setError("")
                }
              >
                ×
              </button>
  
            </div>
          )}
  
          {/* =====================================================
              SUCCESS
          ===================================================== */}
  
          {message && (
            <div className="crypto-wallet-alert success">
  
              <span>
                ✓
              </span>
  
              <p>
                {message}
              </p>
  
              <button
                type="button"
                onClick={() =>
                  setMessage("")
                }
              >
                ×
              </button>
  
            </div>
          )}
  
          {/* =====================================================
              GENERATING WALLET
          ===================================================== */}
  
          {generatingWallet && (
            <div className="wallet-generating">
  
              <div className="deposit-spinner" />
  
              <div>
  
                <strong>
                  Creating wallet...
                </strong>
  
                <span>
                  Please wait
                </span>
  
              </div>
  
            </div>
          )}
  
          {/* =====================================================
              CREATE WALLET
          ===================================================== */}
  
          {!generatingWallet &&
            !address && (
  
            <div className="wallet-create-box">
  
              <div className="create-wallet-logo">
  
                <CoinLogo
                  type={
                    selected.coin
                  }
                />
  
              </div>
  
              <h2>
                Wallet not created yet
              </h2>
  
              <p>
                Your{" "}
                {selected.coin}{" "}
                deposit wallet will
                be created securely
                for your account.
              </p>
  
              <button
                type="button"
                onClick={
                  generateWallet
                }
                disabled={
                  generatingWallet
                }
              >
                Create{" "}
                {selected.coin} Wallet
              </button>
  
            </div>
          )}
  
          {/* =====================================================
              WALLET CONTENT
          ===================================================== */}
  
          {!generatingWallet &&
            address && (
  
            <div className="wallet-content">
  
              {/* =================================================
                  WALLET STATUS
              ================================================= */}
  
              <div className="wallet-status">
  
                <span>
                  ✓
                </span>
  
                Wallet ready
  
              </div>
  
              {/* =================================================
                  STEP 1
                  AMOUNT
              ================================================= */}
  
              {!showDepositDetails &&
                !depositSubmitted && (
  
                <form
                  className="deposit-amount-form"
                  onSubmit={
                    handleContinue
                  }
                >
  
                  <label>
                    Deposit amount
                  </label>
  
                  <div className="amount-input">
  
                    <span>
                      $
                    </span>
  
                    <input
                      type="number"
                      min={
                        selected.min
                      }
                      step="any"
                      value={amount}
                      onChange={(e) => {
  
                        setAmount(
                          e.target.value
                        );
  
                        setError("");
  
                        setMessage("");
  
                      }}
                      placeholder={`Enter Amount Min $${selected.min}`}
                    />
  
                    <span>
                      USD
                    </span>
  
                  </div>
  
                  {/* =================================================
                      AMOUNT SHORTCUTS
                  ================================================= */}
  
                  <div className="amount-shortcuts-wrapper">
  
                    <div
                      className={`amount-shortcuts-scroll ${
                        showAllAmountShortcuts
                          ? "show-all"
                          : ""
                      }`}
                    >
  
                      {AMOUNT_SHORTCUTS
                        .filter(
                          (value) =>
                            value >=
                            selected.min
                        )
                        .map(
                          (
                            value
                          ) => (
  
                            <button
                              key={
                                value
                              }
                              type="button"
                              className={`amount-shortcut ${
                                Number(
                                  amount
                                ) ===
                                value
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() => {
  
                                setAmount(
                                  String(
                                    value
                                  )
                                );
  
                                setError(
                                  ""
                                );
  
                                setMessage(
                                  ""
                                );
  
                              }}
                            >
                              $
                              {value.toLocaleString()}
                            </button>
  
                          )
                        )}
  
                    </div>
  
                    <button
                      type="button"
                      className="amount-shortcuts-more"
                      onClick={() =>
                        setShowAllAmountShortcuts(
                          (prev) =>
                            !prev
                        )
                      }
                      aria-label={
                        showAllAmountShortcuts
                          ? "Hide amount shortcuts"
                          : "Show all amount shortcuts"
                      }
                    >
                      {showAllAmountShortcuts
                        ? "‹"
                        : "›"}
                    </button>
  
                  </div>
  
                  <button
                    type="submit"
                    className="deposit-submit"
                  >
                    Continue / Confirmed
                  </button>
  
                </form>
              )}
  
              {/* =================================================
                  STEP 2
                  QR + ADDRESS
              ================================================= */}
  
              {showDepositDetails &&
                !depositSubmitted && (
  
                <div className="deposit-details">
  
                  {/* QR */}
  
                  <div className="wallet-qr-wrapper">
  
                    <div className="wallet-qr">
  
                      <img
                        src={qrUrl}
                        alt={`${selected.coin} wallet QR`}
                      />
  
                    </div>
  
                    <span>
                      Scan to deposit
                    </span>
  
                  </div>
  
                  {/* ADDRESS TITLE */}
  
                  <div className="wallet-address-title">
  
                    Your{" "}
                    {selected.coin}{" "}
                    deposit address
  
                  </div>
  
                  {/* ADDRESS */}
  
                  <div className="wallet-address-box">
  
                    <span>
                      {address}
                    </span>
  
                    <button
                      type="button"
                      onClick={
                        copyAddress
                      }
                    >
                      {copied
                        ? "✓ Copied"
                        : "Copy"}
                    </button>
  
                  </div>
  
                  {/* WARNING */}
  
                  <div className="wallet-warning">
  
                    <span>
                      ⚠️
                    </span>
  
                    <p>
  
                      Send only{" "}
  
                      <strong>
                        {selected.coin}
                      </strong>{" "}
  
                      on the{" "}
  
                      <strong>
                        {selected.network}
                      </strong>{" "}
  
                      network to this
                      address. Sending
                      another coin or using
                      another network may
                      result in permanent
                      loss.
  
                    </p>
  
                  </div>
  
                  {/* AMOUNT SUMMARY */}
  
                  <div className="deposit-summary">
  
                    <span>
                      Deposit amount
                    </span>
  
                    <strong>
  
                      $
                      {Number(
                        amount
                      ).toFixed(2)}
  
                      {" "}USD
  
                    </strong>
  
                  </div>
  
                  {/* TRANSACTION TYPE */}
  
                  <div className="deposit-summary">
  
                    <span>
                      Status
                    </span>
  
                    <strong>
                      Processing
                    </strong>
  
                  </div>
  
                  {/* CHANGE AMOUNT */}
  
                  <button
                    type="button"
                    className="deposit-change-amount"
                    onClick={() => {
  
                      setShowDepositDetails(
                        false
                      );
  
                      setError("");
  
                      setMessage("");
  
                    }}
                  >
                    ← Change amount
                  </button>
  
                  {/* DEPOSIT BUTTON */}
  
                  <button
                    type="button"
                    className="deposit-submit"
                    disabled={
                      depositLoading
                    }
                    onClick={
                      handleDepositRequest
                    }
                  >
  
                    {depositLoading
                      ? "Submitting..."
                      : "I Have Made The Deposit"}
  
                  </button>
  
                </div>
              )}
  
              {/* =================================================
                  SUBMITTED
              ================================================= */}
  
              {depositSubmitted && (
  
                <div className="deposit-details">
  
                  <div className="wallet-status">
  
                    <span>
                      ✓
                    </span>
  
                    Deposit submitted
  
                  </div>
  
                  <div className="deposit-summary">
  
                    <span>
                      Transaction ID
                    </span>
  
                    <strong>
                      {transactionId}
                    </strong>
  
                  </div>
  
                  <div className="deposit-summary">
  
                    <span>
                      Amount
                    </span>
  
                    <strong>
                      $
                      {Number(
                        amount
                      ).toFixed(2)}
                      {" "}USD
                    </strong>
  
                  </div>
  
                  <div className="deposit-summary">
  
                    <span>
                      Network
                    </span>
  
                    <strong>
                      {selected.network}
                    </strong>
  
                  </div>
  
                  <div className="deposit-summary">
  
                    <span>
                      Status
                    </span>
  
                    <strong>
                      Processing
                    </strong>
  
                  </div>
  
                  <div className="wallet-warning">
  
                    <span>
                      ✓
                    </span>
  
                    <p>
                      Your deposit request
                      has been received and
                      is waiting for
                      confirmation.
                    </p>
  
                  </div>
  
                  <button
                    type="button"
                    className="deposit-submit"
                    onClick={() =>
                      navigate(-1)
                    }
                  >
                    Done
                  </button>
  
                </div>
  
              )}
  
            </div>
          )}
  
          {/* =====================================================
              PROMOTION / BONUS AREA
          ===================================================== */}
  
          <div className="crypto-wallet-page-promo">
  
            {/* =================================================
                MAIN PROMOTION
            ================================================= */}
  
            <div className="crypto-wallet-promo-card crypto-wallet-promo-main">
  
              <div className="crypto-wallet-promo-content">
  
                <span className="crypto-wallet-promo-label">
                  SPECIAL OFFER
                </span>
  
                <h3>
                  Deposit & Get Bonus
                </h3>
  
                <p>
                  Make a qualifying deposit
                  and enjoy exclusive
                  rewards on your account.
                </p>
  
                <button
                  type="button"
                  onClick={() =>
                    navigate("/offers")
                  }
                >
                  View Offers
                </button>
  
              </div>
  
              <div className="crypto-wallet-promo-icon">
                🎁
              </div>
  
            </div>
  
            {/* =================================================
                SMALL PROMOTIONS
            ================================================= */}
  
            <div className="crypto-wallet-promo-grid">
  
              <div className="crypto-wallet-promo-small">
  
                <div className="crypto-wallet-promo-small-icon">
                  ⚡
                </div>
  
                <div>
  
                  <strong>
                    Fast Deposits
                  </strong>
  
                  <span>
                    Secure crypto deposits
                  </span>
  
                </div>
  
              </div>
  
              <div className="crypto-wallet-promo-small">
  
                <div className="crypto-wallet-promo-small-icon">
                  🔒
                </div>
  
                <div>
  
                  <strong>
                    Secure Wallet
                  </strong>
  
                  <span>
                    Your funds stay protected
                  </span>
  
                </div>
  
              </div>
  
            </div>
  
            {/* =================================================
                BONUS OFFERS
            ================================================= */}
  
            <div className="crypto-wallet-bonus-section">
  
              <div className="crypto-wallet-bonus-header">
  
                <span>
                  BONUS PROGRAM
                </span>
  
                <h2>
                  Earn More With Every Deposit
                </h2>
  
                <p>
                  Choose an eligible bonus
                  and maximize your deposit
                  rewards.
                </p>
  
              </div>
  
              {/* FIRST DEPOSIT */}
  
              <div className="crypto-wallet-bonus-card crypto-wallet-bonus-first">
  
                <div className="crypto-wallet-bonus-icon">
                  🎁
                </div>
  
                <div className="crypto-wallet-bonus-content">
  
                  <span className="crypto-wallet-bonus-tag">
                    FIRST DEPOSIT
                  </span>
  
                  <h3>
                    First Deposit Bonus
                  </h3>
  
                  <p>
  
                    Get a bonus of{" "}
  
                    <strong>
                      10% - 98%
                    </strong>{" "}
  
                    on your first
                    qualifying deposit.
  
                  </p>
  
                  <div className="crypto-wallet-bonus-rate">
                    10% - 98%
                  </div>
  
                </div>
  
              </div>
  
              {/* SECOND DEPOSIT */}
  
              <div className="crypto-wallet-bonus-card crypto-wallet-bonus-second">
  
                <div className="crypto-wallet-bonus-icon">
                  💰
                </div>
  
                <div className="crypto-wallet-bonus-content">
  
                  <span className="crypto-wallet-bonus-tag">
                    SECOND DEPOSIT
                  </span>
  
                  <h3>
                    Second Deposit Bonus
                  </h3>
  
                  <p>
  
                    Make your second
                    qualifying deposit
                    and receive{" "}
  
                    <strong>
                      8% - 87%
                    </strong>{" "}
  
                    bonus.
  
                  </p>
  
                  <div className="crypto-wallet-bonus-rate">
                    8% - 87%
                  </div>
  
                </div>
  
              </div>
  
              {/* WEDNESDAY */}
  
              <div className="crypto-wallet-bonus-card crypto-wallet-bonus-wednesday">
  
                <div className="crypto-wallet-bonus-icon">
                  📅
                </div>
  
                <div className="crypto-wallet-bonus-content">
  
                  <span className="crypto-wallet-bonus-tag">
                    EVERY WEDNESDAY
                  </span>
  
                  <h3>
                    Weekly Wednesday Bonus
                  </h3>
  
                  <p>
  
                    Deposit on Wednesday
                    and enjoy a weekly
                    bonus of{" "}
  
                    <strong>
                      5% - 56%
                    </strong>.
  
                  </p>
  
                  <div className="crypto-wallet-bonus-rate">
                    5% - 56%
                  </div>
  
                </div>
  
              </div>
  
            </div>
  
          </div>
  
        </div>
  
      </div>
    );
  }